import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Truck, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle } from "@/pages/InwardStock";
import { useCustomFields, CustomFieldsRenderer, CustomDataCell } from "@/components/CustomFields";

const emptyForm = { name: "", contact_person: "", phone: "", email: "", address: "", gst_number: "", custom_data: {} };

export default function Vendors() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { fields: customFields } = useCustomFields("vendor");

  const load = async () => {
    const r = await api.get("/vendors");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/vendors/${editingId}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", form);
        toast.success("Vendor added");
      }
      closeForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name || "",
      contact_person: v.contact_person || "",
      phone: v.phone || "",
      email: v.email || "",
      address: v.address || "",
      gst_number: v.gst_number || "",
      custom_data: v.custom_data || {},
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try { await api.delete(`/vendors/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="vendors-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Suppliers</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Vendors</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-vendor-btn"><Plus className="w-4 h-4" /> New Vendor</button>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="vendors-table">
          <thead><tr><th>Name</th><th>Contact</th><th>Phone</th><th>Email</th><th>GST</th><th>Address</th>{customFields.length > 0 && <th>Custom</th>}<th></th></tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={customFields.length > 0 ? 8 : 7} className="text-center py-12 text-zinc-500 font-sans"><Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />No vendors yet.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.id}>
                <td className="font-sans font-semibold text-zinc-900">{v.name}</td>
                <td className="font-sans">{v.contact_person || "—"}</td>
                <td>{v.phone || "—"}</td>
                <td className="font-sans">{v.email || "—"}</td>
                <td>{v.gst_number || "—"}</td>
                <td className="font-sans text-zinc-500">{v.address || "—"}</td>
                {customFields.length > 0 && <td><CustomDataCell fields={customFields} data={v.custom_data} /></td>}
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(v)} className="text-zinc-400 hover:text-[#16A34A] transition-colors" data-testid={`edit-vendor-${v.id}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(v.id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`del-vendor-${v.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal onClose={closeForm}>
          <form onSubmit={submit} className="space-y-5" data-testid="vendor-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Vendor" : "New Vendor"}</h2>
              <button type="button" onClick={closeForm} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Vendor Details</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name*"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="v-name" /></Field>
              <Field label="Contact Person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="input-field" data-testid="v-contact" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field font-mono" data-testid="v-phone" /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="v-email" /></Field>
              <Field label="GST Number"><input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="input-field font-mono" data-testid="v-gst" /></Field>
              <Field label="Address" full><textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-field h-20 resize-none" data-testid="v-address" /></Field>
            </div>

            {customFields.length > 0 && (
              <>
                <SectionTitle>Custom Fields</SectionTitle>
                <div className="grid grid-cols-2 gap-4">
                  <CustomFieldsRenderer
                    fields={customFields}
                    values={form.custom_data}
                    onChange={(cd) => setForm({ ...form, custom_data: cd })}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-vendor">{editingId ? "Update Vendor" : "Add Vendor"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
